import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto.ts';
import {
  GET_USER_CONTRIBUTORS_QUERY_KEY,
  GET_USER_CONTRIBUTORS_LIST_QUERY_KEY,
  GET_CONTRIBUTOR_SUMMARY_QUERY_KEY,
} from '@/state/queries/contributors/user-contributors.ts';
import type { PatchContributorRoleSlotDto } from '@/types/dtos/contributors/request/patch-contributor-role-slot.dto.ts';
import { GET_EVENT_CONTRIBUTORS_QUERY_KEY } from '@/state/queries/contributors/use-get-event-contributors';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions';
import { GET_EVENT_FILTERED_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-filtered-event-sessions';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow';
import {
  patchContributorEverywhere,
  resolveContributorEventOriginId,
} from '@/state/mutations/contributors/patch-contributor-cache';

export const usePatchContributorRoleSlotMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patchContributorRoleSlotData: PatchContributorRoleSlotDto) => {
      const token = await getToken();
      return patchContributorRoleSlot(patchContributorRoleSlotData, token ?? '');
    },
    onSuccess: async (data, variables) => {
      try {
        patchContributorEverywhere(queryClient, data, resolveContributorEventOriginId(data));
        // Match usePatchContributorRoleMutation: slot patches affect event contributors (Zustand) and flows/sessions.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [GET_USER_CONTRIBUTORS_QUERY_KEY] }),
          queryClient.invalidateQueries({ queryKey: [GET_USER_CONTRIBUTORS_LIST_QUERY_KEY] }),
          queryClient.invalidateQueries({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] }),
          queryClient.invalidateQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY] }),
          queryClient.invalidateQueries({ queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY] }),
          queryClient.invalidateQueries({ queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY] }),
          queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY] }),
          queryClient.invalidateQueries({ queryKey: [GET_SESSION_QUERY_KEY] }),
          queryClient.invalidateQueries({ queryKey: [GET_CONTRIBUTOR_SUMMARY_QUERY_KEY, variables.contributorId] }),
        ]);
      } catch (error) {
        // Log invalidation errors but don't fail the mutation
        console.error('Error invalidating queries after slot acceptance:', error);
      }
    },
  });
};

const patchContributorRoleSlot = async (
  patchContributorRoleSlotData: PatchContributorRoleSlotDto,
  token: string,
): Promise<ContributorDto> => {
  const { contributorId, roleId, slotId, ...data } = patchContributorRoleSlotData;
  const url = `${import.meta.env.VITE_API_URL}/contributors/${contributorId}/roles/${roleId}/slots/${slotId}`;
  return sendJSONRequest({ url, method: METHOD.PATCH, body: data, token });
};

export default usePatchContributorRoleSlotMutation;
